import React from 'react';
import DOMPurify from 'dompurify';
import useYamlData from '../hooks/useYamlData';
import '../styles/Resume.css';

// resume.yaml carries light inline markup; allow only these tags.
const ALLOWED_TAGS = ['strong', 'i', 'em', 'u'];

const richText = (html) => ({
  __html: DOMPurify.sanitize(html ?? '', { ALLOWED_TAGS, ALLOWED_ATTR: [] }),
});

const Achievements = ({ items, heading = 'Key achievements' }) => (
  <>
    <h4>{heading}</h4>
    <ul>
      {items.map((item, index) => (
        <li key={index} dangerouslySetInnerHTML={richText(item)} />
      ))}
    </ul>
  </>
);

const Resume = () => {
  const { data, loading, error } = useYamlData('/data/resume.yaml');

  if (loading) return <p className="tmc-state">Loading resume...</p>;
  if (error) return <p className="tmc-state tmc-state--error">{error}</p>;

  const { summary, experience, early = [], certification = [], education = [], skills = [] } = data;

  return (
    <div className="tmc-page page-shell resume-page">
      <header className="tmc-page-header">
        <p className="eyebrow">Resume</p>
        <h1>{data.name}</h1>
        <p className="tmc-lede">{data.title}</p>
      </header>

      <section className="tmc-section" aria-labelledby="summary-heading">
        <div className="tmc-section-head">
          <h2 id="summary-heading">Summary</h2>
        </div>
        <div className="tmc-panel">
          <p dangerouslySetInnerHTML={richText(summary.overview)} />
          <blockquote className="resume-quote">{summary.quote}</blockquote>
          <div className="summary-sections">
            {summary.sections.map((section) => (
              <div className="summary-row" key={section.id}>
                <h3>{section.title}</h3>
                <ul>
                  {section.points.map((point, index) => (
                    <li key={index} dangerouslySetInnerHTML={richText(point)} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="expertise-row">
            <strong>Areas of expertise:</strong> {summary.expertise.join(' | ')}
          </p>
        </div>
      </section>

      <section className="tmc-section" aria-labelledby="experience-heading">
        <div className="tmc-section-head">
          <h2 id="experience-heading">Professional experience</h2>
        </div>
        {experience.map((job) => (
          <article className="experience-item" key={job.id}>
            <div className="experience-head">
              <div className="experience-identity">
                <h3 className="company-name">{job.company}</h3>
                <p className="company-summary">{job.summary}</p>
              </div>
              {job.logo && (
                /* White chip for the same reason as the career thread: most of
                   these marks are dark wordmarks that would vanish on the night
                   canvas, and inverting them would wreck their real colours. */
                <span className="experience-logo">
                  <img
                    src={`${process.env.PUBLIC_URL}/logos/${job.logo}`}
                    alt={`${job.company} logo`}
                    loading="lazy"
                  />
                </span>
              )}
            </div>
            <p className="position-period">
              <strong>{job.position}</strong>
              <span className="job-period">{job.period}</span>
            </p>
            <p dangerouslySetInnerHTML={richText(job.description)} />

            {job.focus === 'challenges' && job.challenges ? (
              <>
                <h4>Key challenges</h4>
                {job.challenges.map((challenge) => (
                  <div
                    className="challenge"
                    key={challenge.name}
                    style={{ '--tone': `var(--tmc-signal-${challenge.tone ?? 'systems'})` }}
                  >
                    <p className="challenge-header">
                      <strong>{challenge.name}</strong>
                      <span>{challenge.description}</span>
                    </p>
                    <ul>
                      {challenge.achievements.map((achievement, index) => (
                        <li key={index} dangerouslySetInnerHTML={richText(achievement)} />
                      ))}
                    </ul>
                  </div>
                ))}
              </>
            ) : (
              <Achievements items={job.achievements ?? []} />
            )}
          </article>
        ))}
      </section>

      {certification.length > 0 && (
        <section className="tmc-section" aria-labelledby="certifications-heading">
          <div className="tmc-section-head">
            <h2 id="certifications-heading">Certifications</h2>
          </div>
          <div className="tmc-card-grid tmc-card-grid--narrow">
            {certification.map((cert) => (
              <a
                className="tmc-card certification"
                href={cert.link}
                key={cert.name}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={`/images/certifications/${cert.image}`}
                  alt=""
                  className="certification-image"
                />
                <span className="certification-name">{cert.name}</span>
              </a>
            ))}
          </div>
        </section>
      )}

      <section className="tmc-section" aria-labelledby="early-heading">
        <div className="tmc-section-head">
          <h2 id="early-heading">Early career</h2>
        </div>
        <ul className="early-career">
          {early.map((job) => (
            <li key={`${job.name}-${job.period}`}>
              <span className="early-role">
                <strong>{job.name}</strong> | {job.title}
              </span>
              <span className="tmc-meta">{job.location} | {job.period}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="tmc-section" aria-labelledby="education-heading">
        <div className="tmc-section-head">
          <h2 id="education-heading">Education</h2>
        </div>
        <ul className="education-list">
          {education.map((edu) => (
            <li key={edu.id}>
              <strong>{edu.institution}</strong>
              <span className="education-degree">
                <span dangerouslySetInnerHTML={richText(edu.degree)} />
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="tmc-section" aria-labelledby="skills-heading">
        <div className="tmc-section-head">
          <h2 id="skills-heading">Skills</h2>
          <p className="tmc-lede">
            Grouped by practice rather than alphabetised, and ordered by how current
            the work is.
          </p>
        </div>
        <div className="skill-groups">
          {skills.map((group) => (
            <div
              className={`skill-group${group.current ? ' is-current' : ''}`}
              key={group.group}
            >
              <h3 className="skill-group-title">
                {group.group}
                {group.current && <span className="skill-now">Current focus</span>}
              </h3>
              <ul className="tmc-tag-list">
                {(group.items ?? []).map((item, index) => (
                  <li className="tmc-tag" key={index} dangerouslySetInnerHTML={richText(item)} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Resume;
